<template>
    <div class="wallet-status-chart" :class="{ fullscreen: fullScreen }">
        <div class="title" v-if="fullScreen">
            <ChartDoddleIcon />
            <h2>Wallet Status</h2>
        </div>
        <div class="toolbar">
            <div class="left">
                <div class="buttons">
                    <IconButton
                        :classNames="tableMode ? '' : 'selected'"
                        :onClick="() => (tableMode = false)"
                        v-tippy="{ content: $t('message.chartView') }"
                    >
                        <md-icon>insights</md-icon>
                    </IconButton>
                    <IconButton
                        :classNames="tableMode ? 'selected' : ''"
                        :onClick="() => (tableMode = true)"
                        v-tippy="{ content: $t('message.tableView') }"
                    >
                        <md-icon>table_view</md-icon>
                    </IconButton>
                    <IconButton :onClick="exportToExcel" v-tippy="{ content: $t('message.download') }">
                        <md-icon>download</md-icon>
                    </IconButton>
                    <IconButton :onClick="switchToFullScreenMode" v-tippy="{ content: $t('report.fullScreen') }">
                        <md-icon v-if="!fullScreen">fullscreen</md-icon>
                        <md-icon v-else>fullscreen_exit</md-icon>
                    </IconButton>
                </div>
            </div>
        </div>
        <DoubleBarChart
            :title="$t('report.walletBalanceStatus')"
            :x-axis-data="getXAxisData()"
            :y-axis-data="getYAxisData()"
            :series="getSeries()"
            v-if="!tableMode"
            :override-options="overrideOptions()"
            :key="getChartKey()"
            :auto-height="false"
            :height="500"
        />
        <TableView :cols="getCols()" :rows="getRows()" v-if="tableMode" />
        <div class="stats">
            <StatCard :title="$t('report.totalSpent')" :value="getStats().TotalSpent" unit="₺" />
            <StatCard :title="$t('report.avgSpent')" :value="getStats().AvgSpent" unit="₺" />
            <StatCard :title="$t('report.totalPayment')" :value="getStats().TotalDeposit" unit="₺" />
        </div>
    </div>
</template>
<script>
import Vue from "vue";
import moment from "moment";
import * as echarts from "echarts/lib/echarts";
import _ from "lodash";
import IconButton from "../../Pages/Settings/components/IconButton.vue";
import StackedAreaChart from "../../Pages/Chatbot/pages/reports/charts/StackedAreaChart.vue";
import StatCard from "../../Pages/Chatbot/pages/reports/charts/StatCard.vue";
import TableView from "../../Pages/Chatbot/pages/reports/charts/TableView.vue";
import { expertToExcel } from "../../../utils/utils";
import { dateToHourFromSecondsShortFormat } from "../../../utils/filters";
import { REPORT_API } from "../../../api/statistics";
import log2 from "../../../utils/log2";
import ChartDoddleIcon from "../common/icons/ChartDoddleIcon.vue";
import fscreen from "fscreen";
import AnimatedLineChart from "../../Pages/Chatbot/pages/reports/charts/AnimatedLineChart.vue";
import StringUtils from "../../../utils/StringUtils";
import DoubleBarChart from "../../Pages/Chatbot/pages/reports/charts/DoubleBarChart.vue";

const log = log2("WalletStatus");
export default Vue.extend({
    name: "WalletStatus",
    components: {
        DoubleBarChart,
        AnimatedLineChart,
        ChartDoddleIcon,
        TableView,
        IconButton,
        StatCard,
        StackedAreaChart,
    },
    props: {
        filters: {
            type: Object,
            default: () => ({
                users: [],
                channels: [],
                startDate: moment().startOf("day").toISOString(),
                endDate: moment().endOf("day").toISOString(),
            }),
        },
    },
    data() {
        return {
            stats: {
                TotalVisitorCount: 0,
            },
            tableMode: false,
            loading: true,
            loadingInstance: null,
            graphData: {
                buckets: [],
                timeSeriesType: "hour",
                broadcasts: [],
            },
            fullScreen: false,
            cancels: [],
            tooltip: {
                trigger: "axis",
                axisPointer: {
                    type: "shadow",
                },
                formatter: function (params) {
                    const { value } = params[0];
                    return `${value}₺`;
                },
            },
        };
    },
    methods: {
        overrideOptions() {
            return {
                showBackground: true,
                color: ["#FF0087"],
                dataZoom: [
                    {
                        type: "inside",
                    },
                ],
                animation: "auto",
                animationDuration: 1000,
                animationDurationUpdate: 500,
                animationEasing: "cubicInOut",
                animationEasingUpdate: "cubicInOut",
                animationThreshold: 2000,
                progressiveThreshold: 3000,
                progressive: 400,
                hoverLayerThreshold: 3000,
            };
        },
        switchToFullScreenMode() {
            if (this.fullScreen) {
                this.closeFullScreenMode();
            } else {
                const elem = document.querySelector(".wallet-status-chart");
                fscreen.requestFullscreen(elem);
                this.fullScreen = true;
            }
        },
        closeFullScreenMode() {
            fscreen.exitFullscreen();
        },
        getChartKey() {
            return StringUtils.hashCodeObj({ data: this.graphData, filter: this.filters });
        },
        fetch() {
            this.loading = true;
            this.loadingInstance.show();
            REPORT_API.walletStatus(this.filters)
                .then((res) => {
                    log("walletStatus data fetched", res);
                    this.graphData = res;
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        humanizeDuration(totalSeconds) {
            return dateToHourFromSecondsShortFormat(totalSeconds, this.$i18n.locale, true);
            // return `${hours}h ${minutes}m ${seconds}s`;
        },
        getCols() {
            return [
                {
                    title: this.$t("chatbotv2.date"),
                    dataIndex: "date",
                    key: "date",
                },
                {
                    title: "Toplam Harcama (₺)",
                    dataIndex: "totalSpent",
                    key: "totalSpent",
                },
                {
                    title: "Toplam Yükleme (₺)",
                    dataIndex: "totalDeposit",
                    key: "totalDeposit",
                },
            ];
        },
        getRows() {
            const { buckets, timeSeriesType } = this.graphData;
            log("getRows.buckets", buckets);
            const rows = buckets.map((bucket) => {
                const date = {
                    hour: moment(bucket.date).format("DD/MM/YYYY HH:mm"),
                    day: moment(bucket.date).format("DD/MM/YYYY"),
                    month: moment(bucket.date).format("MM/YYYY"),
                    week: moment(bucket.date).format("YYYY-WW[week]"),
                }[timeSeriesType];
                return {
                    date,
                    totalDeposit: _.round(bucket.totalDeposit, 2),
                    totalSpent: _.round(bucket.totalSpent, 2),
                };
            });
            return rows;
        },
        getStats() {
            const items = this.graphData.buckets;
            const spentDayCount = items.filter((item) => item.totalSpent > 0).length;
            let totalDeposit = _.sumBy(items, "totalDeposit");
            let totalSpent = _.sumBy(items, "totalSpent");
            let avgSpent = spentDayCount > 0 ? totalSpent / spentDayCount : 0;
            totalDeposit = _.round(totalDeposit, 2);
            totalSpent = _.round(totalSpent, 2);
            avgSpent = _.round(avgSpent, 2);
            return {
                TotalDeposit: totalDeposit,
                TotalSpent: totalSpent,
                AvgSpent: avgSpent,
            };
        },
        getXAxisData() {
            const { buckets, timeSeriesType } = this.graphData;
            log("getXAxisData.buckets", buckets);
            const data = buckets.map((item) => {
                if (timeSeriesType === "day") {
                    return moment(item.date).format("DD/MM");
                } else if (timeSeriesType === "hour") {
                    return moment(item.date).format("HH:mm");
                } else if (timeSeriesType === "week") {
                    return moment(item.date).format("YYYY-WW[week]");
                } else {
                    //monthly
                    return moment(item.date).format("MMM");
                }
            });
            return {
                type: "category",
                data,
                show: true,
                axisLabel: {
                    inside: false,
                },
                axisTick: {
                    show: true,
                },
                axisLine: {
                    show: true,
                },
                z: 10,
            };
        },
        getYAxisData() {
            return {
                name: "Ort. Bakiye (₺)",
                type: "value",
                axisLine: {
                    show: true,
                },
                axisTick: {
                    show: true,
                },
                axisLabel: {
                    color: "#999",
                },
            };
        },
        getSeries() {
            const { buckets, timeSeriesType } = this.graphData;
            log("getSeries.buckets", buckets);
            const data = buckets.map((bucket) => bucket.avgBalance);

            return [
                {
                    type: "bar",
                    showBackground: true,
                    itemStyle: {
                        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                            { offset: 0, color: "#83bff6" },
                            { offset: 0.5, color: "#188df0" },
                            { offset: 1, color: "#188df0" },
                        ]),
                    },
                    emphasis: {
                        itemStyle: {
                            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                                { offset: 0, color: "#2378f7" },
                                { offset: 0.7, color: "#2378f7" },
                                { offset: 1, color: "#83bff6" },
                            ]),
                        },
                    },
                    data,
                },
            ];
        },
        exportToExcel() {
            const { buckets, timeSeriesType } = this.graphData;
            log("exportToExcel.buckets", buckets);
            const data = buckets.map((bucket) => {
                const date = {
                    hour: moment(bucket.date).format("DD/MM/YYYY HH:mm"),
                    day: moment(bucket.date).format("DD/MM/YYYY"),
                    month: moment(bucket.date).format("MM/YYYY"),
                    week: moment(bucket.date).format("YYYY-WW[week]"),
                }[timeSeriesType];
                return {
                    date,
                    totalDeposit: _.round(bucket.totalDeposit, 2),
                    totalSpent: _.round(bucket.totalSpent, 2),
                };
            });
            const stats = this.getStats();
            const excelData = [...data];
            expertToExcel(excelData, `website-traffic.xlsx`, "Sheet 1");
        },
    },
    created() {
        const fullscreenCallback = () => {
            console.log("fullscreenCallback", fscreen.fullscreenElement);
            if (fscreen.fullscreenElement !== null) {
                this.fullScreen = true;
            } else {
                this.fullScreen = false;
            }
            this.$forceUpdate();
        };
        fscreen.addEventListener("fullscreenchange", fullscreenCallback);
        this.cancels.push(() => {
            fscreen.removeEventListener("fullscreenchange", fullscreenCallback);
        });
    },
    beforeDestroy() {
        this.cancels.forEach((cancel) => cancel());
    },
    mounted() {
        this.loadingInstance = this.$veLoading({
            target: document.querySelector(".wallet-status-chart"),
            name: "wave",
        });
        this.fetch();
    },
    watch: {
        filters: {
            handler: function (val, oldVal) {
                this.fetch();
            },
            deep: true,
        },
        graphData: {
            handler: function (val, oldVal) {},
            deep: true,
        },
        loading(val) {
            if (val) {
                this.loadingInstance.show();
            } else {
                this.loadingInstance.close();
            }
        },
    },
});
</script>
<style lang="scss">
.wallet-status-chart {
    .title {
        display: flex;
        gap: 4px;
        align-items: center;
        justify-content: center;
        margin: 0;

        h2 {
            text-align: center;
            font-weight: 700;
            font-size: 40px;
            margin: 0 !important;
        }
    }

    &.fullscreen {
        background-color: #fff;
        padding: 20px;
        overflow-y: scroll;

        .stacked-area-chart {
            height: calc(100vh - 200px);
        }
    }
}
</style>
<style scoped lang="scss">
.wallet-status-chart {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .toolbar {
        display: flex;
        justify-content: space-between;

        .left {
            display: flex;

            .buttons {
                display: flex;
                border: 1px solid var(--color-border-default);

                .icon-button {
                    padding: 5px;
                    width: 40px;
                    height: 34px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    border-right: 1px solid var(--color-border-default);
                    border-radius: unset !important;

                    &.selected {
                        background-color: var(--color-bg-info-low-hover);

                        i {
                            color: rgb(55, 162, 255);
                        }
                    }

                    &:hover {
                        background-color: var(--color-bg-neutral-pressed);
                    }
                }

                .icon-button:last-child {
                    border-right: none;
                }
            }
        }

        .right {
            display: flex;
            justify-content: flex-end;
        }
    }

    .stats {
        display: flex;
        flex-direction: row;
        gap: 10px;
        flex-wrap: wrap;
    }
}
</style>
